import { useCallback, useState } from "react";
import type { InvoiceTag } from "../../../models/InvoiceTags";
import { useRejectInvoicesFlow } from "./useRejectInvoicesFlow";
import { useSetInvoiceTags } from "../../../graphql/hooks/invoices/useSetInvoiceTags";

interface Options {
  onFilterChange?: () => void;
  onClearExtra?: () => void;
}

export const useInvoiceList = ({ onFilterChange, onClearExtra }: Options = {}) => {
  const [selectedInvoices, setSelectedInvoices] = useState<string[]>([]);
  const [invoiceDateFrom, setInvoiceDateFrom] = useState<Date | undefined>(undefined);
  const [invoiceDateTo, setInvoiceDateTo] = useState<Date | undefined>(undefined);
  const [dueDateFrom, setDueDateFrom] = useState<Date | undefined>(undefined);
  const [dueDateTo, setDueDateTo] = useState<Date | undefined>(undefined);
  const [selectedAmounts, setSelectedAmounts] = useState<{ min?: number; max?: number } | null>(null);
  const [selectedTag, setSelectedTag] = useState<InvoiceTag | undefined>(undefined);
  const [filterResetKey, setFilterResetKey] = useState(0);
  const [invoiceTagsMap, setInvoiceTagsMap] = useState<Record<string, InvoiceTag[]>>({});

  const { openModal: openRejectModal, modal: rejectModal } = useRejectInvoicesFlow({
    type: "multiple",
    invoiceIds: selectedInvoices,
    onSuccess: () => setSelectedInvoices([]),
  });

  const { setInvoiceTags } = useSetInvoiceTags({
    onCompleted: () => {},
    onError: (e) => console.error("Error setting tags", e),
  });

  const onInvoicesLoaded = useCallback(
    (items: Array<{ id: string; tags?: Array<{ tag: InvoiceTag }> }>) => {
      setInvoiceTagsMap((prev) => ({
        ...prev,
        ...Object.fromEntries(items.map((i) => [i.id, i.tags?.map((t) => t.tag) ?? []])),
      }));
    },
    [],
  );

  const getTagInitialState = (tag: InvoiceTag): boolean | "indeterminate" => {
    const selectedWithTag = selectedInvoices.filter((id) => (invoiceTagsMap[id] ?? []).includes(tag));
    if (selectedWithTag.length === 0) return false;
    if (selectedWithTag.length === selectedInvoices.length) return true;
    return "indeterminate";
  };

  const onApplyTags = async (add: string[], remove: string[]): Promise<void> => {
    const addTags = add as InvoiceTag[];
    const removeTags = remove as InvoiceTag[];
    const results = await Promise.all(
      selectedInvoices.map((invoiceId) => {
        const existing = invoiceTagsMap[invoiceId] ?? [];
        const finalTags = [
          ...existing.filter((t) => !removeTags.includes(t)),
          ...addTags.filter((t) => !existing.includes(t)),
        ];
        return setInvoiceTags({
          variables: { input: { invoiceIds: [invoiceId], tags: finalTags } },
          refetchQueries: ["getClientInvoices"],
        });
      }),
    );
    if (results.some((r) => r?.data?.invoice?.setInvoiceTags?.success !== true)) {
      throw new Error("setInvoiceTags failed");
    }
  };

  const clearTag = () => setSelectedTag(undefined);

  const onToggleTag = (tag: InvoiceTag) => {
    onFilterChange?.();
    setSelectedInvoices([]);
    setSelectedTag((prev) => (prev === tag ? undefined : tag));
  };

  const onSelectDates = (
    { from, to }: { from?: Date; to?: Date },
    type: "invoiceDate" | "dueDate",
  ) => {
    onFilterChange?.();
    setSelectedInvoices([]);
    if (type === "invoiceDate") {
      setInvoiceDateFrom(from);
      setInvoiceDateTo(to);
    } else {
      setDueDateFrom(from);
      setDueDateTo(to);
    }
  };

  const onSelectAmount = (min: number | undefined, max: number | undefined) => {
    onFilterChange?.();
    setSelectedInvoices([]);
    setSelectedAmounts({ min, max });
  };

  const onClearFilters = () => {
    onFilterChange?.();
    setSelectedTag(undefined);
    setInvoiceDateFrom(undefined);
    setInvoiceDateTo(undefined);
    setDueDateFrom(undefined);
    setDueDateTo(undefined);
    setSelectedAmounts(null);
    setSelectedInvoices([]);
    setFilterResetKey((k) => k + 1);
    onClearExtra?.();
  };

  return {
    selectedInvoices,
    setSelectedInvoices,
    invoiceDateFrom,
    invoiceDateTo,
    dueDateFrom,
    dueDateTo,
    selectedAmounts,
    selectedTag,
    filterResetKey,
    openRejectModal,
    rejectModal,
    onInvoicesLoaded,
    getTagInitialState,
    onApplyTags,
    clearTag,
    onToggleTag,
    onSelectDates,
    onSelectAmount,
    onClearFilters,
  };
};
